import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';

import { Modal } from 'ui-components/components/modal';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { Button } from 'ui-components/components/button';
import { ModalFooterCancelButton } from 'ui-components/components/modal/modal-footer/cancel-button';
import { noop } from 'ui-components/default-props/noop';

import ModalPortal from '../../../../../../components/modal-portal';
import { AddFundsConfirmationModalBody } from './body';
import { FundsSummary } from '../../../../../../financial-services-ui/surcharge/funds-summary';

import styles from '../style.module.less';
import messages from '../messages';
import { useTrackWorkflow, WorkflowSteps } from '../../../../../../tracking/use-workflow-tracking';
import { Workflows } from '../../../../../../tracking/tracking-constants';
import { usePaymentSurchargePreview } from '../../../../../../financial-services-ui/surcharge/hooks';
import { RetrySection } from '../../../../../../financial-services-ui/surcharge/retry-section';

export const AddFundsConfirmationModal = ({
  amount,
  currencyCode,
  currentBalance,
  fundActionPromise,
  onClose,
  onFundConfirmTrack = noop,
  onFundSuccess = noop,
  onFundError = noop,
  setAmount,
}) => {
  const [isDisabled, setIsDisabled] = useState(false);
  const trackAddFunds = useTrackWorkflow(Workflows.ADD_FUNDS_FLOW);
  const action = useCallback(async () => {
    const currency = { value: amount, code: currencyCode };
    setIsDisabled(true);
    onFundConfirmTrack({ fundAmount: amount, currencyCode });
    try {
      await fundActionPromise(currency);
      onFundSuccess(amount, currency.code);
    } catch (e) {
      onFundError(e, amount, currency.code);
      trackAddFunds(WorkflowSteps.CANCEL);
    } finally {
      setIsDisabled(false);
      onClose();
    }
  }, [amount, currencyCode, onFundConfirmTrack, fundActionPromise, onFundSuccess, onFundError, trackAddFunds, onClose]);

  const trackAddFundsCancel = useCallback(() => {
    trackAddFunds(WorkflowSteps.CANCEL);
    onClose();
  }, [onClose, trackAddFunds]);

  const {
    loading: isSurchargeLoading,
    totalWithFees,
    creditCardProcessingFeePercentage,
    creditCardProcessingFee,
    error: previewError,
    triggerRetry,
  } = usePaymentSurchargePreview({
    total: amount,
  });

  const isSubmitDisabled = isDisabled || isSurchargeLoading || !!previewError;

  return (
    <ModalPortal onCancel={trackAddFundsCancel} onClose={onClose}>
      <Modal size='small'>
        <ModalHeader className={styles['modal-title']}>
          <FormattedMessage {...messages.addFunds} />
        </ModalHeader>
        <ModalBody className={styles['modal-body']}>
          <AddFundsConfirmationModalBody
            amount={amount}
            currencyCode={currencyCode}
            currentBalance={currentBalance}
            setAmount={setAmount}
            setIsDisabled={setIsDisabled}
          />
          {!previewError && (
            <FundsSummary
              baseAmount={amount}
              className={styles['funds-summary']}
              creditCardProcessingFee={creditCardProcessingFee}
              creditCardProcessingFeePercentage={creditCardProcessingFeePercentage}
              currencyCode={currencyCode}
              totalWithFees={totalWithFees}
            />
          )}
          {!!previewError && <RetrySection isLoading={isSurchargeLoading} triggerRetry={triggerRetry} />}
        </ModalBody>
        <ModalFooter className={styles['modal-footer-background']}>
          <ModalFooterCancelButton className={styles['button-border']} />
          <Button disabled={isSubmitDisabled} onClick={action} theme='primary'>
            <FormattedMessage {...messages.addFunds} />
          </Button>
        </ModalFooter>
      </Modal>
    </ModalPortal>
  );
};

AddFundsConfirmationModal.propTypes = {
  amount: PropTypes.number.isRequired,
  currencyCode: PropTypes.string.isRequired,
  currentBalance: PropTypes.number.isRequired,
  fundActionPromise: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onFundConfirmTrack: PropTypes.func,
  onFundSuccess: PropTypes.func,
  onFundError: PropTypes.func,
  setAmount: PropTypes.func.isRequired,
};
